/**
 * Peer Review Analytics Service
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';

export interface PeerReviewAnalyticsSummary {
  totalPeerReviews: number;
  agreementRatePercent: number;
  discrepancyRatePercent: number;
  pendingPeerReviews: number;
  avgReviewCompletionTimeMinutes: number;
  avgReviewCompletionTimeFormatted: string;
}

export interface PeerReviewQualityTrendPoint {
  label: string;
  bucketKey: string;
  concur: number;
  minorDiscrepancy: number;
  clinicallySignificantDiscrepancy: number;
}

export interface PeerReviewReviewerRow {
  peerReviewerId: number | null;
  peerReviewerName: string;
  reviews: number;
  pending: number;
  concur: number;
  minorDiscrepancy: number;
  clinicallySignificantDiscrepancy: number;
  correctiveActions: number;
  agreementRatePercent: number;
  discrepancyRatePercent: number;
  avgReviewTatMinutes: number;
  avgReviewTatFormatted: string;
}

export interface PeerReviewModalityRow {
  modality: string;
  reviews: number;
  pending: number;
  concur: number;
  minorDiscrepancy: number;
  clinicallySignificantDiscrepancy: number;
  correctiveActions: number;
  avgReviewTatMinutes: number;
  avgReviewTatFormatted: string;
}

export interface PeerReviewAnalyticsFilterOptions {
  modalities: Array<{ value: string; label: string }>;
  radiologists: Array<{ id: number; name: string }>;
  remarks: string[];
  priorities: Array<{ value: string; label: string }>;
  bodyParts: Array<{ value: string; label: string }>;
}

export interface PeerReviewAnalyticsData {
  summary: PeerReviewAnalyticsSummary;
  trend: PeerReviewQualityTrendPoint[];
  modalityRows: PeerReviewModalityRow[];
  reviewerRows: PeerReviewReviewerRow[];
  filterOptions: PeerReviewAnalyticsFilterOptions;
}

export interface PeerReviewAnalyticsCaseRow {
  id: number;
  studyInstanceUid: string | null;
  peerReviewerId: number | null;
  peerReviewerName: string | null;
  modality: string | null;
  remark: string | null;
  status: string;
  priority: string | null;
  bodyPart: string | null;
  createdAt: string | null;
  submittedAt: string | null;
}

export interface PeerReviewAnalyticsCasesData {
  cases: PeerReviewAnalyticsCaseRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PeerReviewAnalyticsCasesFilters extends PeerReviewAnalyticsFilters {
  metric?: string;
  trendBucket?: string;
  drillModality?: string;
  drillReviewerId?: string;
  page?: number;
  limit?: number;
}

export interface PeerReviewAnalyticsFilters {
  startDate: string;
  endDate: string;
  modality?: string;
  radiologistId?: string;
  remark?: string;
  priority?: string;
  bodyPart?: string;
  trendPeriod?: 'day' | 'week' | 'month';
}

export interface PeerReviewAnalyticsServiceResponse {
  success: boolean;
  data?: PeerReviewAnalyticsData;
  error?: string;
  message?: string;
}

function num(v: unknown): number {
  return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
}

function str(v: unknown, fallback = ''): string {
  if (v == null) {
    return fallback;
  }
  if (typeof v === 'string') {
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  return fallback;
}

function normalizeSummary(raw: Record<string, unknown>): PeerReviewAnalyticsSummary {
  return {
    totalPeerReviews: num(raw.totalPeerReviews ?? raw.total_peer_reviews),
    agreementRatePercent: num(raw.agreementRatePercent ?? raw.agreement_rate_percent),
    discrepancyRatePercent: num(raw.discrepancyRatePercent ?? raw.discrepancy_rate_percent),
    pendingPeerReviews: num(raw.pendingPeerReviews ?? raw.pending_peer_reviews),
    avgReviewCompletionTimeMinutes: num(
      raw.avgReviewCompletionTimeMinutes ?? raw.avg_review_completion_time_minutes
    ),
    avgReviewCompletionTimeFormatted: str(
      raw.avgReviewCompletionTimeFormatted ?? raw.avg_review_completion_time_formatted,
      '0 min'
    ),
  };
}

function normalizeTrend(raw: unknown): PeerReviewQualityTrendPoint[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    return {
      label: str(row.label),
      bucketKey: str(row.bucketKey ?? row.bucket_key),
      concur: num(row.concur),
      minorDiscrepancy: num(row.minorDiscrepancy ?? row.minor_discrepancy),
      clinicallySignificantDiscrepancy: num(
        row.clinicallySignificantDiscrepancy ?? row.clinically_significant_discrepancy
      ),
    };
  });
}

function normalizeModalityRows(raw: unknown): PeerReviewModalityRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    const avgMinutes = num(row.avgReviewTatMinutes ?? row.avg_review_tat_minutes);
    return {
      modality: str(row.modality),
      reviews: num(row.reviews),
      pending: num(row.pending),
      concur: num(row.concur),
      minorDiscrepancy: num(row.minorDiscrepancy ?? row.minor_discrepancy),
      clinicallySignificantDiscrepancy: num(
        row.clinicallySignificantDiscrepancy ?? row.clinically_significant_discrepancy
      ),
      correctiveActions: num(row.correctiveActions ?? row.corrective_actions),
      avgReviewTatMinutes: avgMinutes,
      avgReviewTatFormatted: str(
        row.avgReviewTatFormatted ?? row.avg_review_tat_formatted,
        avgMinutes > 0 ? `${avgMinutes} min` : '—'
      ),
    };
  });
}

function normalizeReviewerRows(raw: unknown): PeerReviewReviewerRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    const avgMinutes = num(row.avgReviewTatMinutes ?? row.avg_review_tat_minutes);
    const reviewerIdRaw = row.peerReviewerId ?? row.peer_reviewer_id;
    return {
      peerReviewerId: reviewerIdRaw == null || reviewerIdRaw === '' ? null : num(reviewerIdRaw),
      peerReviewerName: str(row.peerReviewerName ?? row.peer_reviewer_name, 'Unknown'),
      reviews: num(row.reviews),
      pending: num(row.pending),
      concur: num(row.concur),
      minorDiscrepancy: num(row.minorDiscrepancy ?? row.minor_discrepancy),
      clinicallySignificantDiscrepancy: num(
        row.clinicallySignificantDiscrepancy ?? row.clinically_significant_discrepancy
      ),
      correctiveActions: num(row.correctiveActions ?? row.corrective_actions),
      agreementRatePercent: num(row.agreementRatePercent ?? row.agreement_rate_percent),
      discrepancyRatePercent: num(row.discrepancyRatePercent ?? row.discrepancy_rate_percent),
      avgReviewTatMinutes: avgMinutes,
      avgReviewTatFormatted: str(
        row.avgReviewTatFormatted ?? row.avg_review_tat_formatted,
        avgMinutes > 0 ? `${avgMinutes} min` : '—'
      ),
    };
  });
}

function normalizeCaseRows(raw: unknown): PeerReviewAnalyticsCaseRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    const reviewerIdRaw = row.peerReviewerId ?? row.peer_reviewer_id;
    return {
      id: num(row.id),
      studyInstanceUid: str(row.studyInstanceUid ?? row.study_instance_uid) || null,
      peerReviewerId: reviewerIdRaw == null || reviewerIdRaw === '' ? null : num(reviewerIdRaw),
      peerReviewerName: str(row.peerReviewerName ?? row.peer_reviewer_name) || null,
      modality: str(row.modality) || null,
      remark: str(row.remark) || null,
      status: str(row.status),
      priority: str(row.priority) || null,
      bodyPart: str(row.bodyPart ?? row.body_part) || null,
      createdAt: str(row.createdAt ?? row.created_at) || null,
      submittedAt: str(row.submittedAt ?? row.submitted_at) || null,
    };
  });
}

function normalizeFilterOptions(raw: Record<string, unknown>): PeerReviewAnalyticsFilterOptions {
  const modalitiesRaw = raw.modalities;
  const radiologistsRaw = raw.radiologists;
  const prioritiesRaw = raw.priorities;
  const bodyPartsRaw = raw.body_parts ?? raw.bodyParts;
  const remarksRaw = raw.remarks;

  return {
    modalities: Array.isArray(modalitiesRaw)
      ? modalitiesRaw.map(item => {
          const row = (item as Record<string, unknown>) || {};
          return {
            value: str(row.value),
            label: str(row.label, str(row.value)),
          };
        })
      : [],
    radiologists: Array.isArray(radiologistsRaw)
      ? radiologistsRaw.map(item => {
          const row = (item as Record<string, unknown>) || {};
          return {
            id: num(row.id),
            name: str(row.name),
          };
        })
      : [],
    remarks: Array.isArray(remarksRaw) ? remarksRaw.map(String) : [],
    priorities: Array.isArray(prioritiesRaw)
      ? prioritiesRaw.map(item => {
          const row = (item as Record<string, unknown>) || {};
          return {
            value: str(row.value),
            label: str(row.label, str(row.value)),
          };
        })
      : [],
    bodyParts: Array.isArray(bodyPartsRaw)
      ? bodyPartsRaw.map(item => {
          if (item && typeof item === 'object') {
            const row = item as Record<string, unknown>;
            return {
              value: str(row.value),
              label: str(row.label, str(row.value)),
            };
          }
          const value = String(item);
          return { value, label: value };
        })
      : [],
  };
}

function normalizeAnalyticsData(raw: Record<string, unknown>): PeerReviewAnalyticsData {
  return {
    summary: normalizeSummary((raw.summary as Record<string, unknown>) || {}),
    trend: normalizeTrend(raw.trend),
    modalityRows: normalizeModalityRows(raw.modalityRows ?? raw.modality_rows),
    reviewerRows: normalizeReviewerRows(raw.reviewerRows ?? raw.reviewer_rows),
    filterOptions: normalizeFilterOptions(
      (raw.filterOptions as Record<string, unknown>) ||
        (raw.filter_options as Record<string, unknown>) ||
        {}
    ),
  };
}

function normalizeCasesData(raw: Record<string, unknown>): PeerReviewAnalyticsCasesData {
  const paginationRaw = (raw.pagination as Record<string, unknown>) || {};
  return {
    cases: normalizeCaseRows(raw.cases),
    pagination: {
      page: num(paginationRaw.page) || 1,
      limit: num(paginationRaw.limit) || 25,
      total: num(paginationRaw.total),
      totalPages: num(paginationRaw.totalPages ?? paginationRaw.total_pages),
    },
  };
}

function appendPeerReviewAnalyticsFilters(
  params: URLSearchParams,
  filters: PeerReviewAnalyticsFilters
): void {
  params.set('start_date', filters.startDate);
  params.set('end_date', filters.endDate);
  if (filters.modality) {
    params.set('modality', filters.modality);
  }
  if (filters.radiologistId) {
    params.set('radiologist_id', filters.radiologistId);
  }
  if (filters.remark) {
    params.set('remark', filters.remark);
  }
  if (filters.priority) {
    params.set('priority', filters.priority);
  }
  if (filters.bodyPart) {
    params.set('body_part', filters.bodyPart);
  }
  if (filters.trendPeriod) {
    params.set('trend_period', filters.trendPeriod);
  }
}

export function normalizePeerReviewAnalyticsPayload(
  raw: Record<string, unknown>
): PeerReviewAnalyticsData {
  return normalizeAnalyticsData(raw);
}

export function normalizePeerReviewAnalyticsCasesPayload(
  raw: Record<string, unknown>
): PeerReviewAnalyticsCasesData {
  return normalizeCasesData(raw);
}

export async function getPeerReviewAnalytics(
  filters: PeerReviewAnalyticsFilters
): Promise<PeerReviewAnalyticsServiceResponse> {
  try {
    const params = new URLSearchParams();
    appendPeerReviewAnalyticsFilters(params, filters);

    const url = `${API_ENDPOINTS.ANALYTICS.PEER_REVIEW}?${params.toString()}`;
    const response = await apiGet<{
      success?: boolean;
      data?: Record<string, unknown>;
      error?: string;
      message?: string;
    }>(url, { requireAuth: true });

    const apiData = response.data;
    const payload = apiData?.data;

    if (payload && typeof payload === 'object' && apiData?.success !== false) {
      return {
        success: true,
        data: normalizeAnalyticsData(payload),
        message: apiData?.message,
      };
    }

    return {
      success: false,
      error: apiData?.error || 'Invalid response format',
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch peer review analytics';
    return { success: false, error: message };
  }
}

export interface PeerReviewAnalyticsCasesResponse {
  success: boolean;
  data?: PeerReviewAnalyticsCasesData;
  error?: string;
  message?: string;
}

export async function getPeerReviewAnalyticsCases(
  filters: PeerReviewAnalyticsCasesFilters
): Promise<PeerReviewAnalyticsCasesResponse> {
  try {
    const params = new URLSearchParams();
    appendPeerReviewAnalyticsFilters(params, filters);
    if (filters.metric) {
      params.set('metric', filters.metric);
    }
    if (filters.trendBucket) {
      params.set('trend_bucket', filters.trendBucket);
    }
    if (filters.drillModality) {
      params.set('drill_modality', filters.drillModality);
    }
    if (filters.drillReviewerId) {
      params.set('drill_reviewer_id', filters.drillReviewerId);
    }
    if (filters.page) {
      params.set('page', String(filters.page));
    }
    if (filters.limit) {
      params.set('limit', String(filters.limit));
    }

    const url = `${API_ENDPOINTS.ANALYTICS.PEER_REVIEW_CASES}?${params.toString()}`;
    const response = await apiGet<{
      success?: boolean;
      data?: Record<string, unknown>;
      error?: string;
      message?: string;
    }>(url, { requireAuth: true });

    const apiData = response.data;
    const payload = apiData?.data;

    if (payload && typeof payload === 'object' && apiData?.success !== false) {
      return {
        success: true,
        data: normalizeCasesData(payload),
        message: apiData?.message,
      };
    }

    return {
      success: false,
      error: apiData?.error || 'Invalid response format',
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to fetch peer review analytics cases';
    return { success: false, error: message };
  }
}
